import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTrainingSocket as useSocket } from '../../context/TrainingSocketContext';
import { TrainingProgressData } from '../../services/trainingSocketService';
import NetworkVisualizationComponent from './NetworkVisualizationComponent';
import NetworkVisualization2DComponent from './NetworkVisualization2DComponent';
import NetworkVisualization3DComponent from './NetworkVisualization3DComponent';
import { WebGPUNetworkRendererClass } from '../WebGPUNetworkRendererClass';
import { CodeViewer } from '../CodeViewer/CodeViewer';
import { useCodeViewer } from '../../hooks/useCodeViewer';
import ChangeHistoryViewer from './ChangeHistoryViewer';
import ReevaluationModal from './ReevaluationModal';
import DatasetViewDisplay from './DatasetViewDisplay';
import './TrainingProgressDisplay.css';

const TrainingProgressDisplay: React.FC = () => {
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [showWebGPU, setShowWebGPU] = useState(false);
  const [showCodeViewer, setShowCodeViewer] = useState(false);
  const [showPixi2D, setShowPixi2D] = useState(false);
  const [showVisualization, setShowVisualization] = useState(false);
  const [showReevaluationModal, setShowReevaluationModal] = useState(false);
  const [showDatasetView, setShowDatasetView] = useState(false);
  const [compactView, setCompactView] = useState(false);
  const [viewMode, setViewMode] = useState<'3d_depth' | '2d_grid'>('3d_depth');
  const [showEdges, setShowEdges] = useState<boolean>(true);
  const [showWeightDelta, setShowWeightDelta] = useState<boolean>(false); // Toggle for weight visualization mode - DISABLED by default
  const [isMinimized, setIsMinimized] = useState(() => {
    // Load minimized state from localStorage
    try {
      const saved = localStorage.getItem('main_training_minimized');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const webGPURendererRef = useRef<WebGPUNetworkRendererClass | null>(null);

  const {
    isConnected,
    trainingStatus,
    trainingProgressData,
    modelStructure,
    lastTrainingError,
    cancelTraining,
  } = useSocket();

  // Reset minimized state when training starts (status changes from idle to started/progress)
  useEffect(() => {
    if ((trainingStatus === 'started' || trainingStatus === 'progress') && isMinimized) {
      console.log('[TrainingProgressDisplay] Training started, restoring from minimized state');
      setIsMinimized(false);
      try {
        localStorage.setItem('main_training_minimized', 'false');
      } catch (e) {
        console.error('Error saving minimized state:', e);
      }
    }
  }, [trainingStatus, isMinimized]);

  // Memoize onRendererReady callback to prevent unnecessary re-renders
  const handleRendererReady = useCallback((renderer: WebGPUNetworkRendererClass | null) => {
    webGPURendererRef.current = renderer;
  }, []);

  const codeViewer = useCodeViewer();

  const chartData = trainingProgressData;
  const latestData = chartData.length > 0 ? chartData[chartData.length - 1] : null;

  let statusMessage = 'Attempting to connect to server...';
  if (isConnected) {
    switch (trainingStatus) {
      case 'idle':
        statusMessage = 'Ready to start training.';
        break;
      case 'started':
        statusMessage = 'Training initiated, waiting for first epoch data...';
        break;
      case 'progress':
        if (!latestData) {
          statusMessage = 'Training in progress, waiting for data...';
        } else {
          statusMessage = '';
        }
        break;
      case 'completed':
        statusMessage = 'Training completed!';
        break;
      case 'error':
        statusMessage = 'Training encountered an error.';
        break;
      default:
        statusMessage = 'Waiting for training data...';
    }
  }

  // If showing dataset view, render that instead
  if (showDatasetView) {
    console.log('[VIEW] Switched to Dataset View');
    // Calculate height accounting for DashboardLayout top bar (~64px) and content-area padding (24px top)
    const topBarHeight = 64; // Approximate top bar height
    const contentAreaPaddingTop = 24;
    const totalOffset = topBarHeight + contentAreaPaddingTop;
    return (
      <div style={{ 
        width: '100%', 
        height: `calc(100vh - ${totalOffset}px)`, 
        display: 'flex', 
        flexDirection: 'column', 
        marginTop: `-${contentAreaPaddingTop}px`,
        marginLeft: '-24px', 
        marginRight: '-24px', 
        marginBottom: '-24px',
        position: 'relative'
      }}>
        <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: '#1a202c', borderBottom: '1px solid #4a5568', zIndex: 1000, position: 'sticky', top: 0, marginBottom: '0', flexShrink: 0 }}>
          <button
            onClick={() => {
              console.log('[VIEW] Switched back to Training Progress');
              setShowDatasetView(false);
            }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← Back to Metrics
          </button>
          <h1 style={{ margin: 0, color: 'white' }}>Dataset Overview</h1>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <DatasetViewDisplay />
        </div>
      </div>
    );
  }

  // If showing change history, render that instead
  if (showChangeHistory) {
    return (
      <div style={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: '#1a202c', borderBottom: '1px solid #4a5568' }}>
          <button
            onClick={() => setShowChangeHistory(false)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← Back to Metrics
          </button>
          <h1 style={{ margin: 0, color: 'white' }}>Model Change History</h1>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <ChangeHistoryViewer />
        </div>
      </div>
    );
  }

  // If showing code viewer, render that instead
  if (showCodeViewer) {
    return (
      <div style={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: '#1a202c', borderBottom: '1px solid #4a5568' }}>
          <button
            onClick={() => setShowCodeViewer(false)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← Back to Metrics
          </button>
          <h1 style={{ margin: 0, color: 'white' }}>Training Code</h1>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <CodeViewer
            code={codeViewer.code}
            isLoading={codeViewer.isLoading}
            error={codeViewer.error}
            modelType={codeViewer.modelType}
            configPath={codeViewer.configPath}
            onCopy={codeViewer.copyToClipboard}
            onDownload={codeViewer.downloadCode}
          />
        </div>
      </div>
    );
  }

  // If showing WebGPU visualization, render that instead
  if (showWebGPU) {
    // Calculate height accounting for DashboardLayout top bar (~64px) and content-area padding (24px top)
    const topBarHeight = 64; // Approximate top bar height
    const contentAreaPaddingTop = 24;
    const totalOffset = topBarHeight + contentAreaPaddingTop;
    return (
      <div style={{ 
        width: '100%', 
        height: `calc(100vh - ${totalOffset}px)`, 
        display: 'flex', 
        flexDirection: 'column', 
        marginTop: `-${contentAreaPaddingTop}px`,
        marginLeft: '-24px', 
        marginRight: '-24px', 
        marginBottom: '-24px',
        position: 'relative'
      }}>
        <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: '#1a202c', borderBottom: '1px solid #4a5568', zIndex: 1000, position: 'sticky', top: 0, marginBottom: '0', flexShrink: 0 }}>
          <button
            onClick={() => {
              console.log('[VIEW] Switched back to Training Progress');
              setShowWebGPU(false);
            }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← Back to Metrics
          </button>
          <h1 style={{ margin: 0, color: 'white' }}>WebGPU Network Visualization</h1>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => {
                const newViewMode = viewMode === '3d_depth' ? '2d_grid' : '3d_depth';
                setViewMode(newViewMode);
                if (webGPURendererRef.current) {
                  webGPURendererRef.current.setViewMode(newViewMode);
                }
              }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: viewMode === '2d_grid' ? '#10b981' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              title={viewMode === '3d_depth' ? 'Switch to 2D Grid View: Channels side-by-side, no connections' : 'Switch to 3D Depth View: Channels stacked in depth, with connections'}
            >
              {viewMode === '3d_depth' ? '📊 2D Grid' : '📐 3D Depth'}
            </button>
            <button
              onClick={() => {
                const newCompactView = !compactView;
                console.log('[TrainingProgressDisplay] Toggling compactView:', compactView, '->', newCompactView);
                setCompactView(newCompactView);
              }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: compactView ? '#10b981' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              title={compactView ? 'Expand Channels: Show all channels for conv layers (conv2-conv5)' : 'Collapse Channels: Show only first and last channel for conv layers (conv2-conv5)'}
            >
              {compactView ? '🔓 Expand Channels' : '🔒 Collapse Channels'}
            </button>
            <button
              onClick={() => {
                const newShowEdges = !showEdges;
                setShowEdges(newShowEdges);
                if (webGPURendererRef.current) {
                  webGPURendererRef.current.setShowEdges(newShowEdges);
                }
              }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: showEdges ? '#10b981' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              title={showEdges ? 'Hide Connections: Hide all connections between layers' : 'Show Connections: Show all connections between layers'}
            >
              {showEdges ? '🔗 Hide Connections' : '🔗 Show Connections'}
            </button>
            <button
              onClick={() => {
                const newShowWeightDelta = !showWeightDelta;
                setShowWeightDelta(newShowWeightDelta);
                if (webGPURendererRef.current) {
                  webGPURendererRef.current.setShowWeightDelta(newShowWeightDelta);
                }
              }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: showWeightDelta ? '#3b82f6' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'none' // Disabled for now
              }}
              title={showWeightDelta ? 'Show Weight Delta: Visualize weight changes during backprop' : 'Show Absolute Values: Visualize absolute weight values'}
            >
              {showWeightDelta ? '📈 Weight Delta' : '📊 Absolute'}
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative', zIndex: 1, minHeight: 0 }}>
          <NetworkVisualization3DComponent
            modelStructure={modelStructure}
            trainingProgressData={trainingProgressData}
            compactView={compactView}
            onRendererReady={handleRendererReady}
          />
        </div>
      </div>
    );
  }

  // If showing Pixi 2D visualization, render that instead
  if (showPixi2D) {
    console.log('[VIEW] Switched to Pixi 2D Visualization');
    return (
      <div style={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: '#1a202c', borderBottom: '1px solid #4a5568' }}>
          <button
            onClick={() => {
              console.log('[VIEW] Switched back to Training Progress');
              setShowPixi2D(false);
            }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← Back to Metrics
          </button>
          <h1 style={{ margin: 0, color: 'white' }}>2D Network Visualization</h1>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <NetworkVisualization2DComponent
            modelStructure={modelStructure}
            trainingProgressData={trainingProgressData}
          />
        </div>
      </div>
    );
  }

  // If showing visualization, render that instead
  if (showVisualization) {
    console.log('[VIEW] Switched to Network Visualization');
    return (
      <div style={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: '#1a202c', borderBottom: '1px solid #4a5568' }}>
          <button
            onClick={() => {
              console.log('[VIEW] Switched back to Training Progress');
              setShowVisualization(false);
            }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← Back to Metrics
          </button>
          <h1 style={{ margin: 0, color: 'white' }}>Live Training Visualization</h1>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <NetworkVisualizationComponent
            modelStructure={modelStructure}
            trainingProgressData={trainingProgressData}
          />
        </div>
      </div>
    );
  }

  const handleMinimize = () => {
    setIsMinimized(true);
    try {
      localStorage.setItem('main_training_minimized', 'true');
    } catch (e) {
      console.error('Error saving minimized state:', e);
    }
  };

  const handleRestore = () => {
    setIsMinimized(false);
    try {
      localStorage.setItem('main_training_minimized', 'false');
    } catch (e) {
      console.error('Error saving minimized state:', e);
    }
  };

  // If minimized, show minimal indicator
  if (isMinimized) {
    return (
      <div className="training-progress-minimized" onClick={handleRestore}>
        <div className="minimized-indicator">
          <span>Training in Progress</span>
          {latestData && (
            <span className="minimized-status">
              Epoch {latestData.epoch} | Loss: {latestData.train_loss.toFixed(4)}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="training-progress">
      <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 className="training-progress-title">Training Progress</h2>
        </div>
        {(trainingStatus === 'progress' || trainingStatus === 'started') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to cancel training?')) {
                    cancelTraining();
                  }
                }}
                className="training-action-btn training-action-btn-cancel"
                style={{ backgroundColor: '#dc2626', color: 'white' }}
              >
                ⛔ Cancel Training
              </button>
              {trainingStatus === 'progress' && (
                <button
                  onClick={() => setShowWebGPU(true)}
                  className="training-action-btn training-action-btn-gpu"
                >
                  🚀 Render
                </button>
              )}
              <button
                onClick={handleMinimize}
                className="training-action-btn training-action-btn-minimize"
                title="Minimize"
                style={{ 
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                − Minimize
              </button>
            </div>
            {trainingStatus === 'progress' && (
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowChangeHistory(true)}
                  className="training-action-btn training-action-btn-history"
                >
                  📝 Change History
                </button>
                <button
                  onClick={() => {
                    codeViewer.requestCode();
                    setShowCodeViewer(true);
                  }}
                  className="training-action-btn training-action-btn-code"
                >
                  💻 View Code
                </button>
                <button
                  onClick={() => setShowDatasetView(true)}
                  className="training-action-btn training-action-btn-dataset"
                >
                  📊 View Dataset
                </button>
                <button
                  onClick={() => setShowReevaluationModal(true)}
                  className="training-action-btn training-action-btn-reevaluate"
                >
                  🔍 Reevaluate Model
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showReevaluationModal && (
        <ReevaluationModal onClose={() => setShowReevaluationModal(false)} />
      )}

      <div className="training-progress-status">
        Connection Status: {isConnected ? <span className="status-connected">Connected</span> : <span className="status-disconnected">Disconnected</span>}
      </div>

      <div className="training-progress-status">
        Training Status: <span className={
          trainingStatus === 'error' ? "status-error" :
          trainingStatus === 'completed' ? "status-completed" :
          "status-progress"
        }>{trainingStatus}</span>
      </div>

      {lastTrainingError && (
        <div className="training-progress-error">
          <p>Error: {lastTrainingError}</p>
        </div>
      )}

      {latestData && trainingStatus !== 'error' && (
        <div className="training-progress-latest">
          <p><strong>Epoch:</strong> {latestData.epoch}</p>
          <p><strong>Training Loss:</strong> {latestData.train_loss.toFixed(4)}</p>
          <p><strong>Validation Loss:</strong> {latestData.val_loss.toFixed(4)}</p>
        </div>
      )}

      {!latestData && trainingStatus !== 'error' && trainingStatus !== 'idle' && (
        <p>{statusMessage}</p>
      )}
      {trainingStatus === 'idle' && isConnected && <p>{statusMessage}</p>}

      {latestData && trainingStatus === 'progress' && (
        <>
          <div className="training-progress-bar">
            <div className="training-progress-bar-fill" style={{ width: `${Math.min((latestData.epoch / 100) * 100, 100)}%` }}></div>
          </div>
          <div className="training-progress-bar-label">
            <span>Epoch {latestData.epoch} / 100</span>
            <span>{Math.min((latestData.epoch / 100) * 100, 100).toFixed(0)}% Complete</span>
          </div>
        </>
      )}

      <div className="training-progress-chart">
        <svg width="100%" height="100%" viewBox="0 0 500 300" preserveAspectRatio="none">
          <line x1="50" y1="10" x2="50" y2="290" stroke="#4a5568" strokeWidth="1" />
          <line x1="50" y1="290" x2="490" y2="290" stroke="#4a5568" strokeWidth="1" />

          <text x="250" y="298" fontSize="12" fill="#a0aec0" textAnchor="middle">Epoch</text>
          <text x="10" y="150" fontSize="12" fill="#a0aec0" textAnchor="middle" transform="rotate(-90 10 150)">Loss</text>

          {chartData.length > 1 && (
            <>
              <polyline
                fill="none"
                stroke="#66b2b2"
                strokeWidth="2"
                points={chartData.map((d, i) => {
                  const x = 50 + (i / (chartData.length - 1)) * 440;
                  const allLosses = chartData.flatMap(cd => [cd.train_loss, cd.val_loss]).filter(l => typeof l === 'number' && isFinite(l));
                  const maxLoss = allLosses.length > 0 ? Math.max(...allLosses) : 0;
                  const y = 290 - (d.train_loss / (maxLoss === 0 ? 1 : maxLoss * 1.2)) * 280;
                  return `${x},${y}`;
                }).join(' ')}
              />
              <polyline
                fill="none"
                stroke="#ff6347"
                strokeWidth="2"
                points={chartData.map((d, i) => {
                  const x = 50 + (i / (chartData.length - 1)) * 440;
                  const allLosses = chartData.flatMap(cd => [cd.train_loss, cd.val_loss]).filter(l => typeof l === 'number' && isFinite(l));
                  const maxLoss = allLosses.length > 0 ? Math.max(...allLosses) : 0;
                  const y = 290 - (d.val_loss / (maxLoss === 0 ? 1 : maxLoss * 1.2)) * 280;
                  return `${x},${y}`;
                }).join(' ')}
              />
            </>
          )}
        </svg>
      </div>

      <div className="training-progress-legend">
        <div className="legend-item">
          <div className="legend-color train"></div>
          <span>Training Loss</span>
        </div>
        <div className="legend-item">
          <div className="legend-color val"></div>
          <span>Validation Loss</span>
        </div>
      </div>
    </div>
  );
};

export default TrainingProgressDisplay;
